import { Platform, StyleSheet, Text } from 'react-native';

const ANDROID_FONT_WEIGHTS = {
  200: 'Light',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  800: 'Bold',
};

const isWeb = Platform.OS === 'web',
  isIOS = Platform.OS === 'ios',
  isAndroid = Platform.OS === 'android',
  COLOR_CONTRAST = '#ffffff',
  COLOR_ALT_CONTRAST = '#f8f8f8',
  COLOR_DARKEST = '#000000',
  COLOR_PRIMARY = '#ff7a62',
  COLOR_PRIMARY_RGBA_0 = 'rgba(255, 122, 98, 0)',
  COLOR_PRIMARY_RGBA_10 = 'rgba(255, 122, 98, .1)',
  COLOR_PRIMARY_RGBA_30 = 'rgba(255, 122, 98, .3)',
  COLOR_CALL_TO_ACTION = '#007DA6',
  COLOR_ERROR = '#f05050',
  COLOR_SUCCESS = '#00c581',
  COLOR_WARN = '#F7BF57',
  COLOR_TEXT = 'rgb(43, 43, 51)',
  COLOR_LABEL_TEXT = '#162136',
  COLOR_TEXT_LIGHT = '#777777',
  COLOR_TEXT_DISABLED = '#bbbbbb',
  COLOR_ACCENT = '#eeeeee',
  COLOR_ALT_ACCENT = '#dddddd',
  COLOR_DARK_TRANSLUCENT = 'rgba(0, 0, 0, .3)',
  COLOR_DARK_TRANSLUCENT_LITE = 'rgba(0, 0, 0, .1)',
  COLOR_DARK_TRANSLUCENT_EXTRA_LITE = 'rgba(0, 0, 0, .03)',
  COLOR_DARKEST_TRANSLUCENT = 'rgba(22, 33, 54, .9)',
  COLOR_LIGHT_TRANSLUCENT = 'rgba(255,255,255,0.65)',
  COLOR_THEME_DARK_TEXT = '#ffffff',
  COLOR_THEME_DARK_TEXT_LIGHT = '#999999',
  COLOR_GREEN = '#36dbba',
  COLOR_PURPLE = '#c7a0f4',
  COLOR_BLUE = '#07befa',
  COLOR_DRK_PURPLE = '#6562a4',
  COLOR_PINK = '#f67498',
  COLOR_ORANGE = '#f08a5d',
  COLOR_RED_ORANGE = '#ff837b',
  COLOR_YELLOW = '#f7bf57',
  COLOR_LIGHT_BLUE = '#95dce1',
  COLOR_BUTTON = '#2C426B';

export const COLOR = {
  emphasis: COLOR_PRIMARY,
  emphasis_rgba_0: COLOR_PRIMARY_RGBA_0,
  emphasis_rgba_10: COLOR_PRIMARY_RGBA_10,
  emphasis_rgba_30: COLOR_PRIMARY_RGBA_30,
  callToAction: COLOR_CALL_TO_ACTION,
  error: COLOR_ERROR,
  success: COLOR_SUCCESS,
  warn: COLOR_WARN,
  text: COLOR_TEXT,
  labelText: COLOR_LABEL_TEXT,
  textLight: COLOR_TEXT_LIGHT,
  textDisabled: COLOR_TEXT_DISABLED,
  textReversed: COLOR_CONTRAST,
  accent: COLOR_ACCENT,
  altAccent: COLOR_ALT_ACCENT,
  contrast: COLOR_CONTRAST,
  altContrast: COLOR_ALT_CONTRAST,
  shadow: COLOR_DARKEST,
  darkest: COLOR_DARKEST,
  overlay: COLOR_DARK_TRANSLUCENT,
  overlayLight: COLOR_DARK_TRANSLUCENT_LITE,
  overlayExtraLight: COLOR_DARK_TRANSLUCENT_EXTRA_LITE,
  modalBackground: COLOR_DARKEST_TRANSLUCENT,
  drawerOverlay: COLOR_LIGHT_TRANSLUCENT,
  darkTheme: {
    text: COLOR_THEME_DARK_TEXT,
  },
  greenColor: COLOR_GREEN,
  redOrangeColor: COLOR_RED_ORANGE,
  button: COLOR_BUTTON,
};

export const fontMaker = (options = {}) => {
    let { family, weight, style } = Object.assign(
      {
        family: null,
        weight: null,
        style: null,
      },
      options
    );
    if (isIOS) {
      return {
        fontFamily: family,
        fontWeight: weight,
        fontStyle: style || 'normal',
      };
    } else if (isAndroid) {
      const suffix = ANDROID_FONT_WEIGHTS[weight] + (style || '');
      return {
        fontFamily: family + (suffix.length ? `-${suffix}` : ''),
      };
    } else {
      return {
        fontFamily: family,
        fontWeight: weight,
        fontStyle: style || 'normal',
      };
    }
  },
  FONT_SIZE_MICRO = 10,
  FONT_SIZE_TINY = 12,
  FONT_SIZE_SMALL = 14,
  FONT_SIZE_REGULAR = 16,
  FONT_SIZE_LARGE = 18,
  FONT_SIZE_XLARGE = 20,
  FONT_SIZE_HUGE = 24,
  FONT_SIZE_ENORMOUS = 30,
  FONT_SIZE_GINORMOUS = 50,
  FAMILY_NAME = 'Montserrat',
  FONT_EXTRALIGHT = fontMaker({ family: FAMILY_NAME, weight: '200' }),
  FONT_LIGHT = fontMaker({ family: FAMILY_NAME, weight: '300' }),
  FONT_REGULAR = fontMaker({ family: FAMILY_NAME, weight: '400' }),
  FONT_REGULAR_ITALIC = fontMaker({
    family: FAMILY_NAME,
    weight: '400',
    style: 'italic',
  }),
  FONT_MEDIUM = fontMaker({ family: FAMILY_NAME, weight: '500' }),
  FONT_MEDIUM_ITALIC = fontMaker({
    family: FAMILY_NAME,
    weight: '500',
    style: 'italic',
  }),
  FONT_SEMIBOLD = fontMaker({ family: FAMILY_NAME, weight: '600' }),
  FONT_BOLD = fontMaker({ family: FAMILY_NAME, weight: '700' }),
  FONT_EXTRABOLD = fontMaker({ family: FAMILY_NAME, weight: '800' });

export const PARTICIPANT_COLORS = [
    COLOR_GREEN,
    COLOR_PURPLE,
    COLOR_BLUE,
    COLOR_DRK_PURPLE,
    COLOR_PINK,
    COLOR_ORANGE,
    COLOR_RED_ORANGE,
    COLOR_YELLOW,
    COLOR_LIGHT_BLUE,
  ],
  FONT = {
    extralight: FONT_EXTRALIGHT,
    light: FONT_LIGHT,
    regular: FONT_REGULAR,
    regularItalic: FONT_REGULAR_ITALIC,
    medium: FONT_MEDIUM,
    mediumItalic: FONT_MEDIUM_ITALIC,
    semibold: FONT_SEMIBOLD,
    bold: FONT_BOLD,
    extrabold: FONT_EXTRABOLD,
  },
  FONT_SIZE = {
    micro: FONT_SIZE_MICRO,
    tiny: FONT_SIZE_TINY,
    small: FONT_SIZE_SMALL,
    regular: FONT_SIZE_REGULAR,
    large: FONT_SIZE_LARGE,
    xlarge: FONT_SIZE_XLARGE,
    huge: FONT_SIZE_HUGE,
    enormous: FONT_SIZE_ENORMOUS,
    ginormous: FONT_SIZE_GINORMOUS,
  },
  // Specialized use only. Avoid if possible. You should use FONT instead.
  FONT_FAMILY = {
    regular: FAMILY_NAME + (isAndroid ? '-Regular' : ''),
  };

const THEME_BASE = {
  input: {
    flex: 1,
    ...FONT_REGULAR,
    fontSize: FONT_SIZE_LARGE,
    height: 40,
    padding: 0,
    paddingTop: 5,
    color: COLOR.text,
  },
  inputContainerDisabled: {
    borderBottomColor: 'rgb(228, 228, 228)',
  },
  container: {
    flex: 1,
  },
  transparentContainer: {
    flex: 1,
  },
  basicRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  borderBottom: {
    borderColor: '#e4e4e4',
    borderBottomWidth: 1,
  },
  borderTop: {
    borderColor: '#e4e4e4',
    borderTopWidth: 1,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  inputsContainer: {
    paddingHorizontal: 25,
    paddingVertical: 15,
  },
  containerPadding: {
    padding: 25,
  },
  itemVertical: {
    marginBottom: 10,
  },
  errorMessage: {
    ...FONT_MEDIUM,
    fontSize: FONT_SIZE_REGULAR,
    color: COLOR_ERROR,
    marginBottom: 20,
  },
  errorMessageSmall: {
    ...FONT_SEMIBOLD,
    fontSize: FONT_SIZE_SMALL,
    color: COLOR_ERROR,
    marginBottom: 20,
  },
  labelText: {
    ...FONT_BOLD,
    fontSize: FONT_SIZE_TINY,
  },
  removeButton: {
    flex: 1,
    backgroundColor: COLOR_ERROR,
    justifyContent: 'center',
  },
  removeButtonInner: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeButtonIcon: {
    fontSize: FONT_SIZE_ENORMOUS,
    color: COLOR_CONTRAST,
  },
  arrowIcon: {
    fontSize: FONT_SIZE_HUGE,
    color: COLOR_TEXT_LIGHT,
    marginTop: 2,
  },
  checkIcon: {
    fontSize: FONT_SIZE_GINORMOUS,
    color: COLOR_SUCCESS,
  },
  linkStyle: {
    color: COLOR_CALL_TO_ACTION,
  },
  disabled: {
    opacity: 0.5,
  },
  textRegular: {
    ...FONT_REGULAR,
    fontSize: isWeb ? FONT_SIZE_LARGE : FONT_SIZE_LARGE,
    color: COLOR_TEXT,
  },
  textMedium: {
    ...FONT_MEDIUM,
    fontSize: FONT_SIZE_LARGE,
  },
  textSemibold: {
    ...FONT_SEMIBOLD,
    fontSize: FONT_SIZE_LARGE,
  },
  textBold: {
    ...FONT_BOLD,
    fontSize: FONT_SIZE_LARGE,
  },
  subhead: {
    ...FONT_SEMIBOLD,
    fontSize: FONT_SIZE_LARGE,
    marginBottom: 15,
  },
  clarifyingNote: {
    ...FONT_MEDIUM_ITALIC,
    fontSize: FONT_SIZE_REGULAR,
  },
  badgeContainer: {
    position: 'absolute',
    left: Platform.OS === 'ios' ? 0 : Platform.OS === 'web' ? 10 : 5,
    bottom: Platform.OS === 'ios' ? 0 : Platform.OS === 'web' ? 12 : 5,
    width: 20,
    height: 20,
    borderColor: 'white',
    borderWidth: 1,
    borderRadius: 10,
    backgroundColor: COLOR_PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    ...FONT_MEDIUM,
    fontSize: FONT_SIZE_MICRO,
    color: COLOR_CONTRAST,
    textAlign: 'center',
  },
  sectionHeaderText: {
    ...FONT_MEDIUM,
    fontSize: isWeb ? 14 : 10,
    color: '#2f2f38',
  },
  sectionHeader: {
    flexDirection: 'row',
    backgroundColor: COLOR_ALT_CONTRAST,
    borderBottomWidth: 1,
    borderColor: COLOR_ALT_ACCENT,
  },
  sectionHeaderColumn: {
    borderRightWidth: 1,
    borderColor: COLOR_ALT_ACCENT,
    padding: 15,
    justifyContent: 'center',
  },
  rowColumn: {
    paddingHorizontal: 15,
    paddingVertical: 9,
  },
  rowText: {
    ...FONT_SEMIBOLD,
    fontSize: isWeb ? 16 : 12,
    color: COLOR_TEXT,
  },
  rowButtons: isWeb
    ? {
        paddingLeft: 10,
      }
    : {
        justifyContent: 'flex-end',
        paddingRight: 10,
      },
  textBubble: {
    paddingVertical: 2,
    backgroundColor: 'rgb(241, 241, 241)',
    borderWidth: 2,
    borderColor: 'rgb(119, 119, 119)',
    borderRadius: 14,
    marginHorizontal: 5,
    marginVertical: 5,
    alignItems: 'center',
  },
  textBubbleSelected: {
    backgroundColor: '#ffedea',
    borderColor: COLOR.emphasis,
  },
  textBubbleDisabled: {
    opacity: 0.4,
  },
  textBubbleText: {
    ...FONT.regular,
    color: COLOR_TEXT,
    fontSize: 16,
    textAlign: 'center',
  },
  textBubbleTextSelected: {
    ...FONT.semibold,
  },
  shadowSubtle: {
    borderWidth: 1,
    borderColor: '#e3e3e3',
    boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
  },
};

// @hcw not sure where to put this, so stuck it here for now
export const __WEB__ = isWeb;
export const __IOS__ = isIOS;
export const __ANDROID__ = isAndroid;
export const NAVBAR_BUTTON_DIM = isIOS ? 35 : isWeb ? 60 : 50;
export const NAVBAR_BUTTON_DIM_WIDTH = isIOS ? 45 : isWeb ? 60 : 50;

export const SELECT_STYLE = {
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    // const color = chroma(data.color);
    // console.info({ data, isDisabled, isFocused, isSelected });
    return {
      ...styles,
      ...THEME.textRegular,
      backgroundColor: isFocused ? '#2C426B' : null,
      color: isFocused ? '#fff' : COLOR.text,
      overflow: 'hidden',
    };
  },
  container: (base, state) => ({
    ...base,
    flex: 1,
  }),
  valueContainer: (base, state) => ({
    ...base,
    ...THEME.textRegular,
    padding: 0,
  }),
  singleValue: (base, state) => {
    return {
      ...base,
      ...THEME.textRegular,
    };
  },
  placeholder: (base, state) => ({
    ...base,
    ...THEME.textRegular,
  }),
  menu: (base, state) => {
    return {
      ...base,
      zIndex: 9999,
      position: 'absolute',
    };
  },
  menuList: (base, state) => ({
    ...base,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 100000,
  }),
  control: (styles) => {
    return {
      ...styles,
      borderWidth: 0,
      '&:hover': { borderColor: 'rgb(83, 83, 90)' },
      boxShadow: 'none',
      borderBottomWidth: 1,
      borderRadius: 0,
      borderColor: 'rgb(83, 83, 90)',
      paddingLeft: 0,
      paddingRight: 0,
    };
  },
  input: (styles) => {
    return {
      ...styles,
      '&:active': { outline: 'none !important' },
    };
  },
  indicatorSeparator: (styles) => {
    return { ...styles, backgroundColor: 'transparent' };
  },
  dropdownIndicator: (styles) => {
    return {
      ...styles,
      marginRight: -10,
    };
  },
};

export const SELECT_STYLE_DISABLED = {
  control: (styles) => {
    return {
      ...styles,
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderBottomWidth: 1,
      borderRadius: 0,
      borderColor: 'rgb(83, 83, 90)',
      paddingLeft: 0,
      paddingRight: 0,
    };
  },
  dropdownIndicator: (styles) => {
    return {
      ...styles,
      display: 'none',
    };
  },
};

export const SELECT_STYLE_GREYED_OUT = {
  control: (styles) => {
    return {
      ...styles,
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderBottomWidth: 1,
      borderRadius: 0,
      borderColor: 'rgb(228, 228, 228)',
      paddingLeft: 0,
      paddingRight: 0,
    };
  },
};

export const SELECT_STYLE_ROUND = {
  ...SELECT_STYLE,
  control: (styles) => ({ ...styles, borderRadius: 20, borderWidth: 2, paddingLeft: 20 }),
  indicatorSeparator: (styles) => {
    return { ...styles, backgroundColor: 'transparent' };
  },
  dropdownIndicator: (styles) => {
    return {
      ...styles,
      marginRight: 0,
    };
  },
};

// Only use "named" themes where a specific theme is called for. For example
// The "auth" screens should be dark.
export const THEME_LIGHT = {
    ...THEME_BASE,
    container: Object.assign({}, THEME_BASE.container, {
      backgroundColor: COLOR_CONTRAST,
    }),
    containerAlt: Object.assign({}, THEME_BASE.container, {
      backgroundColor: COLOR_ALT_CONTRAST,
    }),
    input: Object.assign({}, THEME_BASE.input, {
      color: 'black',
    }),
    labelText: Object.assign({}, THEME_BASE.labelText, {
      color: COLOR_LABEL_TEXT,
    }),
    textRegularLight: Object.assign({}, THEME_BASE.textRegular, {
      color: COLOR_TEXT_LIGHT,
    }),
    customNavBarBaseStyle: {
      width: NAVBAR_BUTTON_DIM_WIDTH,
      height: NAVBAR_BUTTON_DIM,
      justifyContent: 'center',
      alignItems: 'center',
    },
    customNavBarTextStyle: {
      width: isIOS ? 'auto' : 100,
      height: NAVBAR_BUTTON_DIM,
      justifyContent: 'center',
      alignItems: 'center',
    },
    header: {
      paddingVertical: 15,
      backgroundColor: COLOR.contrast,
    },
    headerText: {
      ...FONT.medium,
      fontSize: FONT_SIZE.xlarge,
      color: COLOR.emphasis,
      textAlign: 'center',
    },
    headerAltContrast: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 20,
      paddingHorizontal: 25,
      backgroundColor: COLOR.altContrast,
      borderColor: '#e4e4e4',
      borderBottomWidth: 1,
    },
    headerAltContrastText: {
      ...FONT.medium,
      fontSize: FONT_SIZE.large,
      color: COLOR.text,
    },
    subhead: Object.assign({}, THEME_BASE.subhead, {
      color: COLOR_TEXT_LIGHT,
    }),
    clarifyingNote: Object.assign({}, THEME_BASE.clarifyingNote, {
      color: COLOR_TEXT,
    }),
  },
  THEME_DARK = {
    ...THEME_BASE,
    container: Object.assign({}, THEME_BASE.container, {
      backgroundColor: COLOR_DARKEST,
    }),
    input: Object.assign({}, THEME_BASE.input, {
      color: COLOR_THEME_DARK_TEXT,
    }),
    labelText: Object.assign({}, THEME_BASE.labelText, {
      color: COLOR_THEME_DARK_TEXT,
    }),
    textRegular: Object.assign({}, THEME_BASE.textRegular, {
      color: COLOR_THEME_DARK_TEXT,
    }),
    textRegularLight: Object.assign({}, THEME_BASE.textRegular, {
      color: COLOR_THEME_DARK_TEXT_LIGHT,
    }),
    textSemibold: Object.assign({}, THEME_BASE.textSemibold, {
      color: COLOR_THEME_DARK_TEXT,
    }),
    subhead: Object.assign({}, THEME_BASE.subhead, {
      color: COLOR_THEME_DARK_TEXT_LIGHT,
    }),
    clarifyingNote: Object.assign({}, THEME_BASE.clarifyingNote, {
      color: COLOR_THEME_DARK_TEXT,
    }),
  },
  // Use this theme as much as possible. It is the base theme
  // for the application.
  THEME = THEME_LIGHT,
  styles = StyleSheet.create(THEME);

export function bold(text) {
  return <Text style={FONT.bold}>{text}</Text>;
}
